import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import useElementWhenReady from "../../hooks/useElementWhenReady";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import GalleryButtonJs from "../buttons/galleryButton/GalleryButton";
import { handleModalTriggerEvent } from "../../utils/handleModalTriggerEvent";
import { useWaysToStyle } from "../../context/waysToStyle/WaysToStyleContext";
import { WAYS_TO_STYLE_OPEN_MODAL_EVENT } from "../../constants";
import {
  fetchStockPriceForLook,
  fireAndForgetStockPriceForOtherLooks,
  getDefaultThumbnailIndex,
} from "./utils";
import { fireWaysToStyleModalOpen } from "../../analytics/waysToStyle/waysToStyleModalOpen";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import styles from "./WaysToStyleGalleryButton.scss";

interface GalleryButtonProps {
  ref?: React.Ref<HTMLButtonElement>;
  iconClass?: string;
  ariaLabel: string;
  buttonText: string;
  handleClick: () => void;
  dataTestId?: string;
}

const GalleryButton =
  GalleryButtonJs as React.ComponentType<GalleryButtonProps>;

const WaysToStyleGalleryButton: React.FC = () => {
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const mobilePortalElement = useElementWhenReady(`#looks-button-portal`);
  const { getTranslation, storeCode } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    storeCode: string;
  };
  const buttonText = getTranslation("pdp_associated_buy_the_look_title");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { shouldShowWaysToStyle, stylingLooks, setStockPriceForLook } =
    useWaysToStyle();

  useEffect(() => {
    if (mobilePortalElement) {
      setShouldShowButton(shouldShowWaysToStyle && stylingLooks.length > 0);
    }
  }, [mobilePortalElement, shouldShowWaysToStyle, stylingLooks]);

  if (!shouldShowButton) {
    return null;
  }

  if (!mobilePortalElement) {
    return null;
  }

  const handleClick = async () => {
    try {
      const firstLook = stylingLooks[0];

      if (!firstLook) {
        return;
      }
      const lookIndex = 0;
      const mergedLook = await fetchStockPriceForLook(firstLook, storeCode);

      setStockPriceForLook(firstLook.id, mergedLook);
      fireWaysToStyleModalOpen({ products: mergedLook.products });
      fireAndForgetStockPriceForOtherLooks(
        stylingLooks,
        storeCode,
        lookIndex,
        setStockPriceForLook,
      );

      handleModalTriggerEvent({
        focusOnCloseElement: buttonRef.current,
        eventKey: WAYS_TO_STYLE_OPEN_MODAL_EVENT,
        data: {
          selectedLook: mergedLook,
          lookIndex,
          thumbnailIndex: getDefaultThumbnailIndex(firstLook.products),
        },
      });
    } catch (error) {
      logError(ERROR_TYPE.StockPriceError, {
        message: "Stock price fetch failed for WaysToStyle gallery button",
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return createPortal(
    <div className={styles.waysToStyleGalleryButtonWrapper}>
      <GalleryButton
        ref={buttonRef}
        iconClass={"product-hanger"}
        ariaLabel={buttonText}
        buttonText={buttonText}
        handleClick={handleClick}
        dataTestId={"looksButtonHero"}
      />
    </div>,
    mobilePortalElement,
  );
};

export default WaysToStyleGalleryButton;
